// Per-asset freshness, judged separately from feed connectivity. Pure, so every combination is unit-testable.
export const STALE_MS = 15000;        // feed-level: a venue silent this long is no longer "live"
export const QUIET_MS = 60000;        // asset-level: connected feed but no trade for >60 s → QUIET (last trade price)
export const ASSET_STALE_MS = 300000; // asset-level: no trade for >5 min → STALE even while the feed is connected
export const POLL_STALE_MS = 75000;   // polled: CoinGecko refresh overdue → STALE

// age is clamped at 0: a venue clock slightly ahead of ours reads "just now", never negative / in the future
export const ageOf = (at, srvNow) => (at == null ? null : Math.max(0, srvNow - at));

export function assessStreamed({ at, feedUp, srvNow }) {
  const age = ageOf(at, srvNow);
  if (!feedUp) return { state: "stale", feed: "disconnected", reason: "feed_down", age };
  if (age < QUIET_MS) return { state: "live", feed: "connected", reason: null, age };
  if (age < ASSET_STALE_MS) return { state: "quiet", feed: "connected", reason: "no_recent_trade", age };
  return { state: "stale", feed: "connected", reason: "no_recent_trade", age };
}

export function assessPolled({ at, srvNow }) {
  const age = ageOf(at, srvNow);
  return age < POLL_STALE_MS ? { state: "polled", feed: "polled", reason: null, age } : { state: "stale", feed: "polled", reason: "poll_overdue", age };
}

// Displayed `at` is shifted onto the viewer's clock (skew = client − server) and never placed in the viewer's future.
export const displayAt = (at, skew, clientNow) => (at == null ? null : Math.min(at + skew, clientNow));

export const SNAPSHOT_DELAYED_MS = 15 * 60000; // a CoinGecko snapshot older than this is labelled "delayed"

// "3 min ago" / "2 h ago" / "5 d ago" — never shows "17280 min ago" for a weeks-old snapshot.
export function ageLabel(ms) {
  if (ms == null || !isFinite(ms)) return null;
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

// Mirror of the server's spark_history(): CoinGecko's 7-day sparkline is hourly and ends at the row's last_updated.
// Returns { points: [[ts, price]...], asOf, reason } — an empty/short series is "unavailable", never drawn as a flat line.
export function sparkHistory(row) {
  if (row?.history) return { points: row.history.points || [], asOf: row.history.as_of || null, reason: row.history.reason || null };
  const prices = (row?.sparkline_in_7d?.price || []).filter((p) => typeof p === "number" && isFinite(p));
  const end = row?.last_updated ? Date.parse(row.last_updated) : NaN;
  if (prices.length < 2 || !isFinite(end)) return { points: [], asOf: row?.last_updated || null, reason: "unavailable" };
  const n = prices.length;
  return { points: prices.map((p, i) => [end - (n - 1 - i) * 3600000, p]), asOf: row.last_updated, reason: null };
}
